// src/app/gallery/[galleryId]/page.tsx
'use client';

import React, { useEffect, useState, useRef, FormEvent } from 'react';
import { normalizeSlug } from '@/lib/seo/slug';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { useRouter, useParams } from 'next/navigation';
import Link from 'next/link';
import { X, ChevronLeft, ChevronRight, Pencil, Trash2, Users, ExternalLink, ShieldAlert, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiDelete, apiFetch, getJson, apiGet, authHeaders } from '@/lib/api';
import { toAbsoluteMediaUrl } from '@/lib/media';
import { ComplianceStatusBadge } from '@/components/compliance/ComplianceStatusBadge';
import { PerformerTaggingModal } from '@/components/content/PerformerTaggingModal';
import { toCdn } from '@/lib/imageUrls';
import Spinner from '@/components/ui/spinner';
import { buildProfileLink } from '@/lib/chatUi';
import InlineBanner from '@/components/ui/inline-banner';
import DetailToolbar from '@/components/common/DetailToolbar';
import MiniTalentCard from '@/components/common/MiniTalentCard';
import CardGridSkeleton from '@/components/skeletons/CardGrid';
import { useConfirm, useAlert } from '@/components/ui/confirm-dialog';
import ImageGallerySlider from '@/components/ImageGallerySlider';
import { scrubContactInfo } from '@/lib/scrubContact';
import CommentsPanelClient from '@/components/CommentsPanelClient';
import AudienceBadge from '@/components/content/AudienceBadge';
import { isPrivateAudience } from '@/lib/audience';

interface ImageDto {
  imageFeedId: number;
  location: string;
  thumbnailUrl: string;
  isUnsafeContent: boolean;
  name: string;
  description: string;
  caption: string;
  approval?: string | null;
}

interface GalleryDto {
  galleryId: number;
  personId: string; // GUID
  name: string;
  description: string;
  images: ImageDto[];
  dateUpdated?: string;
  dateCreated?: string;
  /** 'Public' | 'Friends' | 'Watchers'. Only ever seen by a viewer inside the audience. */
  audience?: string;
}

// The API answers 404 (never 403) for a gallery outside the viewer's audience,
// exactly as it does for a gallery that does not exist. getJson throws
// Error('HTTP 404') for a bodiless 404 and the body message otherwise; apiGet
// style errors carry .status. Treat all of those as "not found".
function isNotFoundError(e: unknown): boolean {
  const anyE = e as any;
  if (anyE?.status === 404) return true;
  const msg = String(anyE?.message ?? '');
  return /\b404\b/.test(msg) || /\bnot[ _-]?found\b/i.test(msg);
}

// Resolve a preferred URL for display given a raw key or absolute URL.
function resolvePreferredUrl(rawKeyOrUrl: string, preferOriginal: boolean = false): string {
  const key = String(rawKeyOrUrl || '');
  if (!key) return '';
  const isAbs = /^https?:\/\//i.test(key);
  if (isAbs) {
    try {
      const u = new URL(key);
      let cleaned = u.pathname.replace(/^\/+/, '');
      if (preferOriginal) cleaned = cleaned.replace(/_thumb(\.[^./]+)?$/i, '$1');
      // If the absolute URL points to an image-feed style path, force FEED CDN host.
      if (/^(image-feed|thumbnails|video-thumbs|thumbs)\//i.test(cleaned)) {
        return toCdn(cleaned, 'feed');
      }
      return key;
    } catch { /* fall through */ }
  }
  let cleaned = key.replace(/^\/+/, '');
  if (preferOriginal) cleaned = cleaned.replace(/_thumb(\.[^./]+)?$/i, '$1');
  if (/^(image-feed|thumbnails|video-thumbs|thumbs)\//i.test(cleaned)) return toCdn(cleaned, 'feed');
  return toAbsoluteMediaUrl(cleaned.startsWith('/') ? cleaned : '/' + cleaned);
}

type AttemptedRef = React.MutableRefObject<Set<string>>;

function GalleryImageTile({
  img,
  onOpen,
  attempted,
  onEdit,
  onDelete,
  showOwnerControls,
  guest,
}: {
  img: ImageDto;
  onOpen: () => void;
  attempted: AttemptedRef;
  onEdit: () => void;
  onDelete: () => void;
  showOwnerControls: boolean;
  guest: boolean;
}) {
  const router = useRouter();
  const rawKey = String((img.thumbnailUrl || img.location || '') || '');
  // Strip _blurred(_timestamp)? suffix so auth users always get the original URL.
  // Guests receive the blurred URL from the API when a BlurredLocation exists;
  // CSS blur is applied as a fallback when no server-side blurred file exists yet.
  const raw = guest ? rawKey : rawKey.replace(/_blurred(_\d+)?(\.[a-z0-9]+(?:\?.*)?$)/i, '$2');
  const explicit = Boolean((img as any)?.isUnsafeContent ?? (img as any)?.IsUnsafeContent ?? false);
  const isPending = img.approval === 'pending_verification';
  // API sends correct blurred URLs for guests - just use as-is
  const preferred = resolvePreferredUrl(raw);
  const [src, setSrc] = useState(preferred);
  useEffect(() => { setSrc(preferred); }, [preferred, img.imageFeedId]);
  return (
    <article
      className="relative group cursor-pointer overflow-hidden rounded-lg w-full"
      onClick={() => {
        if (guest && explicit) {
          try {
            const ret = typeof window !== 'undefined' ? window.location.pathname : '/gallery';
            router.push(`/login?returnTo=${encodeURIComponent(ret)}`);
          } catch {}
          return;
        }
        if (isPending) return; // don't open lightbox for pending images
        onOpen();
      }}
    >
      <div className="relative w-full h-48">
        <Image
          src={src || '/img/default-avatar.svg'}
          alt={img.name}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 25vw"
          className={`object-cover object-center transition-transform duration-200 group-hover:scale-105 rounded-lg${guest && explicit ? ' blur-xl scale-110' : ''}`}
          placeholder="empty"
          onError={() => {
            try {
              const rk = String((img.thumbnailUrl || img.location || '') || '');
              if (!rk) return;
              if (attempted.current.has(rk)) return;
              attempted.current.add(rk);
              const cleaned = rk.replace(/^\/+/, '');
              setSrc(`/media/resolve?k=${encodeURIComponent(cleaned)}&area=feed`);
            } catch {}
          }}
        />
      </div>
      {isPending && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <span className="text-xs font-medium text-white bg-amber-600 px-2 py-0.5 rounded">
            Pending Verification
          </span>
        </div>
      )}
      {guest && explicit && !isPending && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <span className="text-xs font-medium text-white px-2 py-1 bg-black/60 rounded">Sensitive</span>
        </div>
      )}
      {showOwnerControls && !isPending && (
        <div className="absolute inset-0 flex items-center justify-center space-x-3 opacity-0 group-hover:opacity-100 bg-black bg-opacity-30 transition">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100"
            title="Edit Image"
          >
            <Pencil className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
            title="Delete Image"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )}
    </article>
  );
}

function LightboxImage({ image, attempted, guest }: { image: ImageDto; attempted: AttemptedRef; guest?: boolean }) {
  const rawKey = String(image.location || image.thumbnailUrl || '');
  const raw = guest ? rawKey : rawKey.replace(/_blurred(_\d+)?(\.[a-z0-9]+(?:\?.*)?$)/i, '$2');
  const preferred = resolvePreferredUrl(raw, true);
  const [src, setSrc] = useState(preferred);
  useEffect(() => { setSrc(preferred); }, [preferred, image.imageFeedId]);
  return (
    <div className="relative w-full max-h-[80vh] grid place-items-center">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src || '/img/default-avatar.svg'}
        alt={image.name}
        className="max-h-[80vh] w-auto h-auto object-contain object-center rounded-lg shadow-lg"
        onError={() => {
          try {
            const rk = String(image.location || image.thumbnailUrl || '');
            if (!rk) return;
            if (attempted.current.has(rk)) return;
            attempted.current.add(rk);
            const cleaned = rk.replace(/^\/+/, '');
            setSrc(`/media/resolve?k=${encodeURIComponent(cleaned)}&area=feed`);
          } catch {}
        }}
      />
    </div>
  );
}

export default function GalleryPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const { galleryId } = useParams() as { galleryId?: string };
  const numericGalleryId = parseInt(galleryId || '0', 10);
  const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL || '';

  // Minimal dev log
  if (process.env.NODE_ENV !== 'production') {
    try { console.log('GalleryPage render → status:', status); } catch {}
  }

  const [gallery, setGallery] = useState<GalleryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const attemptedFallback = useRef<Set<string>>(new Set());
  const loadedIdRef = useRef<number | null>(null);
  const [related, setRelated] = useState<any[] | null>(null);
  const viewPingedRef = useRef<number | null>(null);
  const [viewCount, setViewCount] = useState<number>(0);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [creator, setCreator] = useState<{ name?: string; avatarUrl?: string; profileUrl?: string } | null>(null);
  const confirmModal = useConfirm();
  const alertModal = useAlert();

  // 2257 Compliance state
  const [complianceRecord, setComplianceRecord] = useState<{ id: number; complianceStatus: string; multiplePerformersDetected: boolean; detectedPersonCount: number } | null>(null);
  const [showPerformerModal, setShowPerformerModal] = useState(false);

  useEffect(() => {
    let canceled = false;
    async function loadGallery() {
      setError(null);

      let fetchedGallery: GalleryDto | null = null;

      // Fetch public gallery only if this id hasn't been loaded yet (prevents flashing)
      const needPublic = loadedIdRef.current !== numericGalleryId;
      if (needPublic) {
        setLoading(true);
        try {
          const publicData = await getJson<GalleryDto & { owner?: { id?: string; displayName?: string; avatarUrl?: string } }>(`/api/gallery/${numericGalleryId}`);
          if (canceled) return;
          fetchedGallery = publicData as any;
          loadedIdRef.current = numericGalleryId;
          // Reset metrics on id change (clear stale values before applying any persisted)
          setViewCount(0);
          setLikeCount(0);
          setIsLiked(false);
          setGallery(fetchedGallery);
          try {
            // Fallback: seed creator from gallery name + personId so the mini card is not empty
            // Prefer numeric owner.personId for profile link when present
            const own = (fetchedGallery as any)?.owner;
            if (own && (own.personId != null)) {
              setCreator((prev) => prev || {
                name: own.displayName || (fetchedGallery as any).name || undefined,
                avatarUrl: own.avatarUrl || undefined,
                profileUrl: (() => { const sl = normalizeSlug(own.displayName || ''); return (sl && sl.length >= 3) ? `/u/${sl}` : `/details/${encodeURIComponent(String(own.personId))}`; })(),
              });
            }
          } catch {}
          try {
            const anyG = fetchedGallery as any;
            if (typeof anyG?.likeCount === 'number') setLikeCount(anyG.likeCount);
            if (typeof anyG?.viewCount === 'number') setViewCount(anyG.viewCount);
            if (typeof anyG?.isLiked === 'boolean') setIsLiked(anyG.isLiked);
            if (anyG?.owner) {
              setCreator({
                name: anyG.owner.displayName || undefined,
                avatarUrl: anyG.owner.avatarUrl || undefined,
                profileUrl: (typeof anyG.owner.personId !== 'undefined' && anyG.owner.personId != null)
                  ? (() => { const sl = normalizeSlug(anyG.owner.displayName || ''); return (sl && sl.length >= 3) ? `/u/${sl}` : `/details/${encodeURIComponent(String(anyG.owner.personId))}`; })()
                  : (anyG.owner.userId ? buildProfileLink(String(anyG.owner.userId), anyG.owner.displayName) : undefined),
              })
            }
          } catch {}
          viewPingedRef.current = null;
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') console.error('Public gallery fetch error:', e);
          if (!canceled) {
            if (isNotFoundError(e)) {
              // Missing OR outside this viewer's audience: same answer on
              // purpose. The 'Gallery not found' branch below says nothing
              // about friends or watchers, so a non-audience viewer learns
              // nothing about the gallery's existence. loadedIdRef is left
              // unset so the effect re-fetches once auth settles: a friend
              // whose token was not ready on first paint must not be stuck
              // on the guest answer.
              setGallery(null);
            } else {
              setError('Failed to load gallery.');
            }
            setLoading(false);
          }
          return;
        } finally {
          if (!canceled) setLoading(false);
        }
      }

      // 2) If authenticated AND IDs match, fetch owner version without toggling loading
      const base = fetchedGallery ?? gallery;
      if (
        status === 'authenticated' &&
        session?.user?.id &&
        base?.personId &&
        session.user.id === base.personId
      ) {
        try {
          const ownerData = await getJson<GalleryDto & { owner?: { id?: string; displayName?: string; avatarUrl?: string } }>(`/api/gallery/${numericGalleryId}?userGuid=${session.user.id}`);
          if (!canceled) {
            setGallery(ownerData);
            try {
              const anyG = ownerData as any;
              if (typeof anyG?.likeCount === 'number') setLikeCount(anyG.likeCount);
              if (typeof anyG?.viewCount === 'number') setViewCount(anyG.viewCount);
              if (typeof anyG?.isLiked === 'boolean') setIsLiked(anyG.isLiked);
              // Update creator seed if still missing (prefer numeric owner.personId)
              if ((ownerData as any)?.owner?.personId != null) {
                setCreator((prev) => prev || {
                  name: (ownerData as any)?.owner?.displayName || (ownerData as any).name || undefined,
                  avatarUrl: (ownerData as any)?.owner?.avatarUrl || undefined,
                  profileUrl: (() => { const sl = normalizeSlug((ownerData as any)?.owner?.displayName || ''); return (sl && sl.length >= 3) ? `/u/${sl}` : `/details/${encodeURIComponent(String((ownerData as any).owner.personId))}`; })(),
                });
              }
              if (anyG?.owner) {
                setCreator({
                  name: anyG.owner.displayName || undefined,
                  avatarUrl: anyG.owner.avatarUrl || undefined,
                  profileUrl: (typeof anyG.owner.personId !== 'undefined' && anyG.owner.personId != null)
                    ? (() => { const sl = normalizeSlug(anyG.owner.displayName || ''); return (sl && sl.length >= 3) ? `/u/${sl}` : `/details/${encodeURIComponent(String(anyG.owner.personId))}`; })()
                    : (anyG.owner.userId ? buildProfileLink(String(anyG.owner.userId), anyG.owner.displayName) : undefined),
                })
              }
            } catch {}
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') console.error('Owner fetch failed:', e);
        }
      }

      // Fire a view ping once per gallery id (no loading toggle)
      if (viewPingedRef.current !== numericGalleryId) {
        viewPingedRef.current = numericGalleryId;
        try {
          // Use apiFetch so this works in production (not just via dev rewrite)
          apiFetch(`/api/gallery/${numericGalleryId}/view`, { method: 'POST', cache: 'no-store' } as any).catch(() => {});
          setViewCount((v) => v + 1);
        } catch {}
      }
    }

    loadGallery();
    return () => { canceled = true; };
  }, [status, session?.user?.id, numericGalleryId]);

  // After gallery load, fetch like state for the current user (if authenticated)
  useEffect(() => {
    let canceled = false;
    (async () => {
      if (status !== 'authenticated') return;
      if (!gallery) return;
      try {
        const data = await apiGet<{ likeCount: number; isLiked?: boolean }>(`/api/gallery/${numericGalleryId}/likes`, session as any);
        if (canceled) return;
        if (typeof data.likeCount === 'number') setLikeCount(data.likeCount);
        if (typeof data.isLiked === 'boolean') setIsLiked(data.isLiked);
      } catch {
        /* optional */
      }
    })();
    return () => { canceled = true; };
  }, [status, session, gallery?.galleryId, numericGalleryId]);

  // Fetch creator info for toolbar mini card (consistent across details)
  React.useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        // Prefer numeric personId from owner (public data includes it); fall back to GUID
        const pid = ((gallery as any)?.owner?.personId != null)
          ? String((gallery as any).owner.personId)
          : ((gallery && gallery.personId) ? String(gallery.personId) : null);
        if (!pid) { setCreator(null); return; }
        // Prefer /api/people/{id}; fallback to /api/person/{id}
        let name: string | undefined;
        let avatarUrl: string | undefined;
        try {
          const j: any = await getJson(`/api/people/${encodeURIComponent(pid)}`);
          name = j?.displayName ?? j?.DisplayName ?? j?.stageName ?? j?.StageName ?? j?.firstName ?? j?.FirstName ?? undefined;
          avatarUrl = j?.avatarUrl ?? j?.AvatarUrl ?? j?.profileImageLocation ?? j?.ProfileImageLocation ?? undefined;
        } catch {
          try {
            // Fallback: resolve GUID via person detail endpoint that accepts GUID
            const j: any = await getJson(`/api/person/${encodeURIComponent(pid)}/detail`);
            const p = j?.person || j;
            name = p?.stageName ?? p?.displayName ?? p?.StageName ?? p?.DisplayName ?? p?.firstName ?? p?.FirstName ?? undefined;
            avatarUrl = p?.profileImageLocation ?? p?.ProfileImageLocation ?? p?.avatarUrl ?? p?.AvatarUrl ?? undefined;
          } catch {
            name = undefined; avatarUrl = undefined;
          }
        }
        if (!canceled) {
          const sl = normalizeSlug(name || '');
          const profileUrl = (sl && sl.length >= 3) ? `/u/${sl}` : (/^\d+$/.test(String(pid)) ? `/details/${encodeURIComponent(String(pid))}` : buildProfileLink(String(pid), name));
          setCreator({ name, avatarUrl, profileUrl });
        }
      } catch { if (!canceled) setCreator(null); }
    })();
    return () => { canceled = true; };
  }, [gallery?.personId]);

  // Determine owner status (GUID vs GUID)
  const isOwner = Boolean(
    session?.user?.id && gallery?.personId && session.user.id === gallery.personId
  );

  // Dev-only ownership log
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      try { console.log('isOwner:', isOwner); } catch {}
    }
  }, [isOwner]);

  // Fetch 2257 compliance data for owner's gallery
  useEffect(() => {
    if (!isOwner || !gallery) return;
    let canceled = false;
    (async () => {
      try {
        const data = await apiGet<any>(`/api/content/2257?contentType=Gallery&contentId=${gallery.galleryId}`, session as any);
        if (canceled) return;
        const rec = Array.isArray(data) ? data[0] : data;
        if (rec?.id) setComplianceRecord(rec);
      } catch { /* 2257 record may not exist */ }
    })();
    return () => { canceled = true; };
  }, [isOwner, gallery?.galleryId, session]);

  const toggleLike = async () => {
    if (status !== 'authenticated') {
      try { await alertModal({ title: 'Sign in required', message: 'Sign in to like this gallery.' }); } catch {}
      return;
    }
    const nextLiked = !isLiked;
    setIsLiked(nextLiked);
    setLikeCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    try {
      if (nextLiked) {
        await apiFetch(`/api/gallery/${numericGalleryId}/like`, session, { method: 'POST' });
      } else {
        await apiFetch(`/api/gallery/${numericGalleryId}/like`, session, { method: 'DELETE' });
      }
    } catch (e) {
      setIsLiked(!nextLiked);
      setLikeCount((c) => Math.max(0, c + (nextLiked ? -1 : 1)));
    }
  };

  const onDeleteGallery = async () => {
    const ok = await confirmModal({
      title: 'Delete gallery?',
      message: 'Are you sure you want to delete this entire gallery?',
      confirmLabel: 'Delete',
      // cancel button is always rendered in dialog; label fixed
      confirmType: 'danger',
    });
    if (!ok) return;
    try {
      await apiDelete(`/api/gallery/${numericGalleryId}`, session);
      router.push('/profile/galleries');
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error(e);
      setError('Failed to delete gallery.');
    }
  };

  const onUploadImage = async (e: FormEvent) => {
    e.preventDefault();
    if (!fileInputRef.current?.files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('File', fileInputRef.current.files![0]);
      formData.append('Name', uploadName);
      formData.append('Description', uploadDescription);
      formData.append('Caption', uploadCaption);

      const resp = await apiFetch(`/api/gallery/${numericGalleryId}/image`, session, { method: 'POST', body: formData });
      if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
      const newImage = (await resp.json()) as ImageDto;
      setGallery((prev) =>
        prev ? { ...prev, images: [newImage, ...prev.images] } : prev
      );
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error(e);
      setError('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const onDeleteImage = async (imageFeedId: number) => {
    const ok = await confirmModal({ title: 'Delete image?', message: 'This image will be removed from the gallery.', confirmLabel: 'Delete', confirmType: 'danger' });
    if (!ok) return;
    try {
      await apiDelete(`/api/gallery/${numericGalleryId}/image/${imageFeedId}`, session);
      setGallery((prev) =>
        prev
          ? {
              ...prev,
              images: prev.images.filter((i) => i.imageFeedId !== imageFeedId),
            }
          : prev
      );
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error(e);
      setError('Failed to delete image.');
    }
  };

  // Normalize incoming image key/URL to prefer feed CDN when DB stored bare filenames
  const normalizeKey = (s: string): string => {
    if (!s) return s;
    const val = String(s).trim();
    // absolute URL → leave as-is
    if (/^https?:\/\//i.test(val)) return val;
    const cleaned = val.replace(/^\/+/, '');
    if (/^image-feed\//i.test(cleaned)) return cleaned;
    // If looks like a bare filename (no slash, has extension), prefix image-feed/
    if (!cleaned.includes('/') && /\.(jpe?g|png|webp|gif|bmp|tiff)$/i.test(cleaned)) {
      return `image-feed/${cleaned}`;
    }
    return cleaned;
  };

  const openLightbox = (idx: number) => {
    setLightboxIndex(idx);

    // Track image view
    if (gallery && gallery.images[idx]) {
      const image = gallery.images[idx];
      apiFetch(`/gallery/${gallery.galleryId}/image/${image.imageFeedId}/view`, {
        method: 'POST',
      }).catch(() => {}); // Fire and forget
    }
  };
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => {
    if (lightboxIndex === null || !gallery) return;
    setLightboxIndex((lightboxIndex + gallery.images.length - 1) % gallery.images.length);
  };
  const nextImage = () => {
    if (lightboxIndex === null || !gallery) return;
    setLightboxIndex((lightboxIndex + 1) % gallery.images.length);
  };

  // Lightbox a11y: focus management and keyboard controls
  useEffect(() => {
    if (lightboxIndex === null) return;

    // Focus close button on open
    const t = setTimeout(() => {
      try { closeBtnRef.current?.focus(); } catch {}
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevImage();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextImage();
        return;
      }
      if (e.key === 'Tab') {
        // Simple focus trap within lightbox controls
        const root = lightboxRef.current;
        if (!root) return;
        const focusables = Array.from(root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.hasAttribute('disabled'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !root.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [lightboxIndex, prevImage, nextImage]);

  // Lock background scroll while lightbox is open
  useEffect(() => {
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) return;
    const prev = body.style.overflow;
    if (lightboxIndex !== null) body.style.overflow = 'hidden';
    return () => { body.style.overflow = prev; };
  }, [lightboxIndex]);

  if (loading) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
        <div className="h-4 w-48 skeleton rounded" />
        <div className="rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-lg skeleton" />
            <div className="h-3 w-40 skeleton rounded" />
          </div>
          <div className="h-10 md:h-12 w-2/3 skeleton rounded" />
          <div className="mt-3 h-4 w-1/2 skeleton rounded" />
        </div>
        <CardGridSkeleton count={9} />
        <p className="sr-only" aria-live="polite">Loading gallery…</p>
      </main>
    );
  }
  if (error) return (
    <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
      <InlineBanner variant="error">{error}</InlineBanner>
    </main>
  );
  if (!gallery) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
        <nav className="text-sm text-zinc-500 dark:text-zinc-400">
          <Link href="/gallery" className="hover:underline">Galleries</Link>
        </nav>
        <div className="rounded-3xl border border-zinc-200/60 bg-white p-10 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
          <h1 className="font-serif text-2xl font-semibold text-zinc-900 dark:text-zinc-50">Gallery not found</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">It may have been removed or never existed.</p>
        </div>
      </main>
    );
  }

  // JSON-LD: CollectionPage + ItemList(ImageObject)
  const site = (process.env.NEXT_PUBLIC_SITE_BASE_URL || '').replace(/\/$/, '')
  const pageUrl = `${site}/gallery/${encodeURIComponent(String(numericGalleryId))}`
  const imgs = Array.isArray(gallery?.images) ? gallery!.images : []
  const imageObjects = imgs.slice(0, 24).map((im: any) => ({
    '@type': 'ImageObject',
    name: String(im?.name || im?.caption || 'Image'),
    caption: (im?.caption || im?.description || undefined),
    contentUrl: resolvePreferredUrl(String(im?.location || im?.thumbnailUrl || ''), true),
    thumbnail: resolvePreferredUrl(String(im?.thumbnailUrl || im?.location || '')),
  }))
  const collectionJson = { '@context': 'https://schema.org', '@type': 'CollectionPage', name: String(gallery?.name || `Gallery #${numericGalleryId}`), url: pageUrl } as any
  const breadcrumbJson = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${site}/` },
      { '@type': 'ListItem', position: 2, name: 'Galleries', item: `${site}/gallery` },
      { '@type': 'ListItem', position: 3, name: String(gallery?.name || 'Gallery'), item: pageUrl },
    ],
  } as any;

  // Resolve gallery owner info for the creator card + hero
  const galleryOwner: any = (gallery as any)?.owner || {};
  const ownerNameForCard = galleryOwner.displayName || creator?.name || gallery.name || undefined;
  const firstImgForOwner = Array.isArray(gallery.images) && gallery.images.length > 0 ? gallery.images[0] : null;
  const ownerAvatarForCard = galleryOwner.avatarUrl || creator?.avatarUrl || (firstImgForOwner ? (firstImgForOwner.thumbnailUrl || firstImgForOwner.location || '') : '') || undefined;
  const ownerSlug = normalizeSlug(ownerNameForCard || '');
  const ownerProfileUrl = (ownerSlug && ownerSlug.length >= 3)
    ? `/u/${ownerSlug}`
    : (galleryOwner.personId != null)
      ? `/details/${encodeURIComponent(String(galleryOwner.personId))}`
      : (galleryOwner.userId ? buildProfileLink(String(galleryOwner.userId), ownerNameForCard) : (creator?.profileUrl || (gallery.personId ? buildProfileLink(String(gallery.personId), ownerNameForCard) : undefined)));
  const ownerIdForCard = galleryOwner.personId != null ? galleryOwner.personId : (gallery.personId || galleryOwner.userId);

  return (
    <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
      <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJson) }} />
      <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJson) }} />
      {imageObjects.length > 0 && (
        <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@type': 'ItemList', itemListElement: imageObjects.map((o, i) => ({ '@type': 'ListItem', position: i + 1, item: o })) }) }} />
      )}

      {/* Breadcrumb */}
      <nav className="text-sm text-zinc-500 dark:text-zinc-400" aria-label="Breadcrumb">
        <Link href="/gallery" className="hover:underline">Galleries</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <span className="font-semibold text-zinc-900 dark:text-zinc-100">{gallery.name || `Gallery #${gallery.galleryId}`}</span>
      </nav>

      {/* Hero card */}
      <section className="relative overflow-hidden rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl min-w-0">
            <div className="mb-3 inline-flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/10">
                <ImageIcon className="h-4 w-4 text-violet-400" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Gallery · {gallery.images?.length || 0} {gallery.images?.length === 1 ? 'photo' : 'photos'}
                {gallery.dateCreated ? ` · ${new Date(gallery.dateCreated).toLocaleDateString()}` : ''}
                {viewCount > 0 ? ` · ${viewCount.toLocaleString()} ${viewCount === 1 ? 'view' : 'views'}` : ''}
              </span>
              {/* Only a viewer inside the audience ever gets this far, so the
                  label tells them why they can see it. Nothing for Public. */}
              {isPrivateAudience(gallery.audience) && (
                <>
                  <span aria-hidden className="text-xs text-zinc-500 dark:text-zinc-400">·</span>
                  <AudienceBadge
                    audience={gallery.audience}
                    variant="inline"
                    className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400"
                  />
                </>
              )}
            </div>
            <h1 className="font-serif text-[clamp(1.75rem,5vw,2.75rem)] font-semibold leading-[1.1] tracking-tight text-zinc-900 dark:text-zinc-50">
              {scrubContactInfo(gallery.name || `Gallery #${gallery.galleryId}`)}
            </h1>
            {gallery.description && (
              <p className="mt-3 max-w-lg text-balance text-base text-zinc-600 dark:text-zinc-300 sm:text-lg">
                {scrubContactInfo(gallery.description)}
              </p>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center shrink-0">
            <Button asChild variant="outline" className="gap-2">
              <Link href="/gallery">
                <ChevronLeft className="h-4 w-4" aria-hidden />
                <span>All galleries</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Like / Share / Report toolbar */}
      <DetailToolbar
        type="gallery"
        id={gallery.galleryId}
        title={gallery.name}
        author={{
          id: (galleryOwner?.personId ?? gallery.personId),
          name: (ownerNameForCard) as any,
          avatarUrl: (ownerAvatarForCard) as any,
          profileUrl: (ownerProfileUrl) as any,
        }}
        liked={isLiked}
        likeable
        share
        report
        juicyTitle={false}
        variant="banner"
      />

      {/* Creator card */}
      {(galleryOwner?.personId != null || creator?.name || creator?.avatarUrl) && (
        <div className="rounded-3xl border border-zinc-200/60 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-6">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Created by</div>
          <MiniTalentCard
            id={ownerIdForCard as any}
            name={ownerNameForCard as any}
            avatarUrl={ownerAvatarForCard as any}
            profileUrl={ownerProfileUrl as any}
          />
        </div>
      )}

      {/* Owner controls (edit/delete/repair) */}
      {isOwner && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button asChild variant="outline" className="gap-2">
            <Link href={`/profile/galleries/edit/${gallery.galleryId}`}>
              <Pencil className="h-4 w-4" aria-hidden />
              <span>Edit</span>
            </Link>
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="gap-2"
            onClick={onDeleteGallery}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            <span>Delete</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              try {
                await apiFetch(`/api/Gallery/${numericGalleryId}/repair-thumbs`, session as any, { method: 'POST' });
                const publicData = await getJson<GalleryDto>(`/api/gallery/${numericGalleryId}`);
                setGallery(publicData);
              } catch {/* ignore */}
            }}
          >
            Repair Thumbnails
          </Button>
        </div>
      )}

      {/* 2257 Compliance Banner for multi-person content */}
      {isOwner && complianceRecord?.multiplePerformersDetected && complianceRecord.complianceStatus !== 'Compliant' && (
        <section className="mb-4 p-4 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-900/20">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Multiple people detected ({complianceRecord.detectedPersonCount} persons)
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                Tag performers for 2257 compliance. Each performer needs to sign a model release.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => setShowPerformerModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-orange-600 text-white hover:bg-orange-700"
                >
                  <Users className="w-3.5 h-3.5" />
                  Tag Performers
                </button>
                <ComplianceStatusBadge status={complianceRecord.complianceStatus} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Performer Tagging Modal */}
      {complianceRecord && (
        <PerformerTaggingModal
          content2257RecordId={complianceRecord.id}
          isOpen={showPerformerModal}
          onClose={() => setShowPerformerModal(false)}
          onTagged={() => {
            // Refresh compliance data
            apiGet<any>(`/api/content/2257?contentType=Gallery&contentId=${gallery?.galleryId}`, session as any)
              .then((data: any) => { const rec = Array.isArray(data) ? data[0] : data; if (rec?.id) setComplianceRecord(rec); })
              .catch(() => {});
          }}
        />
      )}

      {/* Pending verification banner for owner */}
      {isOwner && gallery.images.some((img: any) => img.approval === 'pending_verification') && (
        <InlineBanner variant="warning" className="mb-4">
          <ShieldAlert className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          Some images in this gallery are not visible to others — explicit content requires{' '}
          <Link href="/profile/verifyaccount" className="underline font-medium">ID verification</Link> before it goes live.
        </InlineBanner>
      )}

      {/* Empty gallery notice for non-owner (all images may be held) */}
      {!isOwner && gallery.images.length === 0 && (
        <InlineBanner variant="info" className="mb-4">
          This gallery has no visible content yet.
        </InlineBanner>
      )}

      {/* Image grid (guests see blurred sensitive thumbnails) */}
      <section className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mb-8">
        {gallery.images.map((img, idx) => (
          <GalleryImageTile
            key={img.imageFeedId}
            img={img}
            attempted={attemptedFallback}
            onOpen={() => openLightbox(idx)}
            onEdit={() => router.push(`/profile/galleries/edit/${gallery.galleryId}`)}
            onDelete={() => onDeleteImage(img.imageFeedId)}
            showOwnerControls={isOwner}
            guest={status === 'unauthenticated'}
          />
        ))}
      </section>

      {/* Related galleries rail (More from this creator) */}
      {(((gallery as any)?.owner?.personId != null) || gallery.personId) && (
        <section className="mt-12 mb-12">
          <div className="max-w-4xl mx-auto px-2">
            <RelatedGalleries ownerPersonId={(((gallery as any)?.owner?.personId != null) ? (gallery as any).owner.personId : (gallery as any)?.personId) as any} currentGalleryId={gallery.galleryId} />
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/80"
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
        >
          <div ref={lightboxRef} className="relative max-w-[96vw] max-h-[90vh] flex items-center justify-center">
            <button
              ref={closeBtnRef}
              onClick={closeLightbox}
              className="fixed top-4 right-4 z-[1001] text-white hover:text-gray-200 p-3 rounded-full bg-black/70 backdrop-blur-sm"
              aria-label="Close"
            >
              <X size={28} />
            </button>
            <button
              ref={prevBtnRef}
              onClick={prevImage}
              className="absolute left-4 text-white hover:text-gray-200 p-2 rounded-full bg-black/60 z-10"
              aria-label="Previous image"
            >
              <ChevronLeft size={32} />
            </button>
            <LightboxImage image={gallery.images[lightboxIndex]} attempted={attemptedFallback} guest={status === 'unauthenticated'} />
            <button
              ref={nextBtnRef}
              onClick={nextImage}
              className="absolute right-4 text-white hover:text-gray-200 p-2 rounded-full bg-black/60 z-10"
              aria-label="Next image"
            >
              <ChevronRight size={32} />
            </button>
          </div>
        </div>
      )}

      {/* Upload New Image Form (only for owner) */}
      {isOwner && (
        <section className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-md shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            Upload New Image
          </h2>
          <form onSubmit={onUploadImage} className="space-y-4">
            <fieldset>
              <legend className="sr-only">New Image Upload</legend>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                File
              </label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="block w-full text-gray-700 dark:text-gray-300"
              />
            </fieldset>
            <fieldset>
              <legend className="sr-only">Image Name</legend>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                type="text"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                required
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </fieldset>
            <fieldset>
              <legend className="sr-only">Image Description</legend>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                rows={3}
              />
            </fieldset>
            <fieldset>
              <legend className="sr-only">Image Caption</legend>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Caption
              </label>
              <input
                type="text"
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </fieldset>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Upload Image'}
            </button>
          </form>
        </section>
      )}

      <CommentsPanelClient id={numericGalleryId} resource="gallery" />
    </main>
  );
}

function RelatedGalleries({ ownerPersonId, currentGalleryId }: { ownerPersonId: number | string; currentGalleryId: number }) {
  const [items, setItems] = React.useState<any[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const base = (process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:5151').replace(/\/+$/, '');
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ah = await authHeaders();
        const res = await fetch(`${base}/api/gallery?personId=${encodeURIComponent(String(ownerPersonId))}&limit=12`, { cache: 'no-store', headers: ah });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        const arr = Array.isArray(list) ? list.filter((g: any) => g?.galleryId !== currentGalleryId && String(g?.name || '').toLowerCase() !== 'chat library') : [];
        if (!cancelled) setItems(arr);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load related galleries');
      }
    })();
    return () => { cancelled = true; };
  }, [ownerPersonId, currentGalleryId]);

  if (error || !items || items.length === 0) return null;
  return (
    <ImageGallerySlider galleries={items} title="More from this creator" slidesPerView={2} />
  );
}

